import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import { memo, useRef } from 'react';
import styled, { css } from 'styled-components';

const ELEMENT_PADDING = 8; //px
const ELEMENT_SIZE = 16; //px

interface IScrollablePagination {
	className?: string;
	totalPages: number;
	currentPage: number;
	onPageChange: (newPage: number) => unknown;
	onLimitChange: (newTotalNumber: number) => unknown;
}

interface IPageNumber {
	className?: string;
	onClick?: () => void;
	state?: 'active' | 'default';
	virtualItem: VirtualItem;
	isActive?: boolean;
}

const ScrollablePagination = ({
	className,
	currentPage,
	totalPages,
	onPageChange,
}: IScrollablePagination) => {
	const parentRef = useRef(null);

	const rowVirtualizer = useVirtualizer({
		count: totalPages,
		getScrollElement: () => parentRef.current,
		estimateSize: () => ELEMENT_SIZE + ELEMENT_PADDING,
		overscan: 4,
	});

	return (
		<ScrollableSection ref={parentRef} className={className}>
			<PaginationWrapper scrollTotalSize={rowVirtualizer.getTotalSize()}>
				{rowVirtualizer.getVirtualItems().map((virtualItem) => (
					<PaginationNumber
						isActive={currentPage === virtualItem.index}
						onClick={() => onPageChange(virtualItem.index)}
						virtualItem={virtualItem}
						key={virtualItem.key as string}
					/>
				))}
			</PaginationWrapper>
		</ScrollableSection>
	);
};

const ScrollableSection = styled.div`
	max-height: 6.5em;
	overflow: auto;
	margin-top: 0.25em;
	margin-bottom: 0.5em;
	::-webkit-scrollbar {
		width: 0.125em;
		height: 0.5em;
	}

	::-webkit-scrollbar-thumb {
		height: 2em;
		background-color: ${({ theme }) => theme.v2.color.neutral['400']};
	}
`;

interface IPaginationWrapper {
	scrollTotalSize: number;
}

const PaginationWrapper = styled.div<IPaginationWrapper>`
	height: ${({ scrollTotalSize }) => `${scrollTotalSize - ELEMENT_PADDING}px`};
	width: 100%;
	position: relative;
`;

const PaginationNumberInternal = ({ className, virtualItem, onClick }: IPageNumber) => {
	return (
		<div className={className}>
			<Text onClick={onClick} variant="sans/11/600" disableVariantMapping>
				{virtualItem.index + 1}
			</Text>
		</div>
	);
};

const PaginationNumber = styled(PaginationNumberInternal)`
	position: absolute;
	top: 0;
	left: 0;
	height: ${({ virtualItem }) => `${virtualItem.size - ELEMENT_PADDING}px`};
	transform: ${({ virtualItem }) => `translateY(${virtualItem.start}px)`};

	border-radius: 0.125em;
	padding: 0 0.25em;

	display: flex;
	align-items: center;
	justify-content: center;

	cursor: pointer;

	${({ isActive }) => {
		if (isActive) {
			return css`
				background-color: ${({ theme }) => theme.v2.color.secondary[800]};
				color: ${({ theme }) => theme.v2.color.neutral['200']};
			`;
		}
		return css`
             &:hover {
                 background-color: ${({ theme }) => theme.v2.color.neutral['300']};
             }}`;
	}}
`;
const _ScrollablePaginationExport = memo(ScrollablePagination);

export { _ScrollablePaginationExport as ScrollablePagination };
